import React, { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import ImpersonationBanner from './components/ImpersonationBanner';
import ErrorBoundary from './components/ErrorBoundary';
import Landing from './pages/Landing';
import Pricing from './pages/Pricing';
import CheckoutSuccess from './pages/CheckoutSuccess';
import CheckoutCancel from './pages/CheckoutCancel';
import Dashboard from './pages/Dashboard';
import Login from './pages/Login';
import SettingsPage from './pages/SettingsPage';
import CollectionPage from './pages/CollectionPage';
import FavoritesPage from './pages/FavoritesPage';
import PlaybackPage from './pages/PlaybackPage';
import PublicProfilePage from './pages/PublicProfilePage';
import AdminLayout from './components/AdminLayout';
import AdminDashboard from './pages/AdminDashboard';
import TierListPage from './pages/TierListPage';
import UserListPage from './pages/UserListPage';
import AuditLogPage from './pages/AuditLogPage';
import FeatureFlagsPage from './pages/FeatureFlagsPage';
import AdminSettingsPage from './pages/AdminSettingsPage';
import AdminNewsletterPage from './pages/AdminNewsletterPage';
import AdminDeniedTagsPage from './pages/AdminDeniedTagsPage';
import AdminReportsPage from './pages/AdminReportsPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import ContactPage from './pages/ContactPage';
import usePageTracking from './hooks/usePageTracking';
import { initGA, trackTimeOnPage, trackError } from './services/analytics';
import { useState } from 'react';

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-dark-900">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-dark-900">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!user.isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

export default function App() {
  // initialize GA once on app mount and enable page tracking via hook
  useEffect(() => {
    initGA();
  }, []);

  // global page time and error tracking
  useEffect(() => {
    /* c8 ignore start */
    if (typeof window === 'undefined') return undefined;
    /* c8 ignore stop */
    let start = Date.now();

    const sendTime = () => {
      try {
        const seconds = Math.round((Date.now() - start) / 1000);
        trackTimeOnPage(window.location.pathname, seconds);
      } catch (e) {
        // swallow
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        sendTime();
        start = Date.now();
      }
    };

    const onBeforeUnload = () => {
      sendTime();
    };

    const onError = (msg, url, lineNo, colNo, err) => {
      try {
        /* c8 ignore start */
        trackError('unhandled_error', String(msg), { url, lineNo, colNo, stack: err && err.stack });
        /* c8 ignore stop */
      /* c8 ignore start */
      } catch (e) {}
      /* c8 ignore stop */    };

    const onRejection = (ev) => {
      try {
        const reason = ev && ev.reason ? ev.reason : ev;
        trackError('unhandled_promise_rejection', String(reason && reason.message ? reason.message : reason), { reason });
      } catch (e) {}
    };

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('beforeunload', onBeforeUnload);
    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);

    return () => {
      sendTime();
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('beforeunload', onBeforeUnload);
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onRejection);
    };
  }, []);

  // call hook to track SPA page views
  usePageTracking();

  // Fetch public site settings (footer link toggles) so routes can be disabled
  const [footerLinks, setFooterLinks] = useState({ privacyPolicy: true, termsOfService: true, contact: true });
  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        const mod = await import('./services/api');
        if (!mounted) return;
        /* c8 ignore start */
        const fn = mod && mod.fetchSiteSettingsPublic;
        if (typeof fn !== 'function') return;
        const data = await fn();
        if (!mounted) return;
        if (data && data.footerLinks) {
          setFooterLinks({
            privacyPolicy: typeof data.footerLinks.privacyPolicy === 'boolean' ? data.footerLinks.privacyPolicy : true,
            termsOfService: typeof data.footerLinks.termsOfService === 'boolean' ? data.footerLinks.termsOfService : true,
            contact: typeof data.footerLinks.contact === 'boolean' ? data.footerLinks.contact : true,
          });
        }
        /* c8 ignore stop */
      } catch (e) {
        // ignore errors fetching optional settings
      }
    })();

    return () => { mounted = false; };
  }, []);

  return (
    <>
      <ImpersonationBanner />
      <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/pricing" element={<Pricing />} />
      {footerLinks.privacyPolicy ? (
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
      ) : (
        <Route path="/privacy-policy" element={<Navigate to="/" replace />} />
      )}
      {footerLinks.termsOfService ? (
        <Route path="/terms-of-service" element={<TermsOfServicePage />} />
      ) : (
        <Route path="/terms-of-service" element={<Navigate to="/" replace />} />
      )}
      {footerLinks.contact ? (
        <Route path="/contact" element={<ContactPage />} />
      ) : (
        <Route path="/contact" element={<Navigate to="/" replace />} />
      )}
      <Route path="/checkout/success" element={<CheckoutSuccess />} />
      <Route path="/checkout/cancel" element={<CheckoutCancel />} />
      <Route path="/login" element={<Login />} />
      <Route path="/u/:username" element={<PublicProfilePage />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/collection"
        element={
          <ProtectedRoute>
            <CollectionPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/favorites"
        element={
          <ProtectedRoute>
            <FavoritesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/playback"
        element={
          <ProtectedRoute>
            <PlaybackPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/*"
        element={
          <AdminRoute>
            <ErrorBoundary>
              <AdminLayout />
            </ErrorBoundary>
          </AdminRoute>
        }
      >
        <Route index element={<AdminDashboard />} />
        <Route path="tiers" element={<TierListPage />} />
        <Route path="users" element={<UserListPage />} />
        <Route path="newsletter" element={<AdminNewsletterPage />} />
        {/* Denied Tags moved into Admin Settings as a tab; route removed */}
        <Route path="reports" element={<AdminReportsPage />} />
        <Route path="audit" element={<AuditLogPage />} />
        <Route path="features" element={<FeatureFlagsPage />} />
        <Route path="settings" element={<AdminSettingsPage />} />
      </Route>
      </Routes>
    </>
  );
}
